import { FunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import { View } from '@/types';
import { trackFeature } from '@/utils/adobeLaunch';
import { t } from '@/utils/i18n';
import { getProductTypeImageUrl } from '@/utils/imageUrls/getProductTypeImageUrl';
import { getPrintAreaForView } from '@/model/productType/getPrintAreaForView';
import { isMacroView } from '@/model/productType/isMacroView';
import { useReduxDraftDispatch } from '@/redux/draft/draftDispatch';
import { getConfigurationErrorsForView } from '@/redux/draft/getters/getConfigurationErrorsForView';
import { getCurrentAppearance } from '@/redux/draft/getters/getCurrentAppearance';
import { getCurrentView } from '@/redux/draft/getters/getCurrentView';
import { getProductType } from '@/redux/draft/getters/getProductType';
import { selectSortedConfigurationsOnView } from '@/redux/draft/getters/selectSortedConfigurationsOnView';
import { StoreState } from '@/redux/types';
import { setViewAction } from '@/components/draft/draftState/actions/setViewAction';
import { Viewer } from '../draft/Viewer';
import './ProductViewSelector.scss';

interface ProductViewSelectorItemProps {
  view: View;
  selected: boolean;
  size: number;
}

const ProductViewSelectorItem: FunctionComponent<ProductViewSelectorItemProps> = ({
  view,
  selected,
  size,
}) => {
  const appearance = useSelector(getCurrentAppearance);
  const productType = useSelector(getProductType);
  const configurations = useSelector(selectSortedConfigurationsOnView(view.id));
  const configurationErrors = useSelector((state: StoreState) =>
    getConfigurationErrorsForView(state, view.id)
  );
  const dispatch = useReduxDraftDispatch();

  const printArea = getPrintAreaForView(productType, view, configurations);

  const macroImageParams = {
    width: 50,
    height: 50,
  };
  const productTypeImage = getProductTypeImageUrl(view, appearance.id, macroImageParams);

  const handleViewSelect = () => {
    trackFeature('product-view');
    dispatch(setViewAction(view));
  };

  return (
    <>
      <button
        type="button"
        onClick={handleViewSelect}
        className={cn('product-view-selector__views', {
          'product-view-selector__views--active': selected,
          'product-view-selector__views--invalid':
            !selected && configurationErrors && configurationErrors.length,
        })}
        role="option"
        aria-selected={selected}
      >
        {isMacroView(view) ? (
          <div
            className={cn('product-view-selector__macro', {
              'product-view-selector__macro--active': selected,
            })}
          >
            <img src={productTypeImage} alt={t('draft.nav.view.material')} height={50} width={50} />
          </div>
        ) : (
          <Viewer
            view={view}
            appearance={appearance}
            printArea={printArea}
            className="product-view-selector__viewer"
            configurations={configurations}
            productTypeImageSize={size}
            hideMask={true}
          />
        )}
        <span className="product-view-selector__viewname">
          {isMacroView(view) ? t('draft.nav.view.material') : view.name}
        </span>
      </button>
    </>
  );
};

export const ProductViewSelector: FunctionComponent<{ size?: number }> = ({ size = 50 }) => {
  const productType = useSelector(getProductType);
  const currentView = useSelector(getCurrentView);

  if (productType.views.length === 1) {
    return null;
  }

  return (
    <ul className="product-view-selector" role="listbox">
      {productType.views.map((view) => (
        <li className="product-view-selector__item" key={view.id}>
          <ProductViewSelectorItem view={view} selected={view.id === currentView.id} size={size} />
        </li>
      ))}
    </ul>
  );
};
